// v-hint — generic hover/focus tooltip styled to match the sidebar's collapsed-rail
// hint (.nav-tip in istar-pages.css). Replaces plain title="…"/:title="…" attributes,
// which render as the browser's own unstyled OS tooltip.
//
// Usage: <button v-hint="$t('some.hint')">…</button>  — falsy value = no tooltip.
//
// ⚠️ Does NOT work on a `disabled` form control: browsers suppress mouse/focus events
// on disabled controls, while a native title= still rendered there. Use `aria-disabled`
// + a guard in the click handler instead (FinanceReport's preset chips and chart bars
// do exactly that) — never move such a hint back to title=.
//
// One singleton floating element is reused for the whole app (same idea as App.vue's
// single .nav-tip element) and positioned via plain DOM, not Vue reactivity — this
// keeps it independent from IdModal/IdSelect's own <teleport> popups and avoids
// re-render churn on every hover.

let tipEl = null;
let ownerEl = null;
let scrollBound = false;

function ensureTipEl() {
  if (!tipEl) {
    tipEl = document.createElement('div');
    tipEl.className = 'sys-tip';
    tipEl.style.display = 'none';
    document.body.appendChild(tipEl);
  }
  return tipEl;
}

// title= used to give an icon-only control its accessible name. A floating div gives
// none, so mirror the text into aria-label when the element has no name of its own.
// "Has a name" = an aria-label/labelledby, or text content containing an actual letter
// (the bell's unread-count badge is digits only, so it still needs the label).
function syncA11y(el, text) {
  if (el.__hintOwnsAria) {
    if (text) el.setAttribute('aria-label', text);
    else { el.removeAttribute('aria-label'); el.__hintOwnsAria = false; }
    return;
  }
  if (!text) return;
  if (el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby')) return;
  if (/\p{L}/u.test(el.textContent || '')) return;
  el.setAttribute('aria-label', text);
  el.__hintOwnsAria = true;
}

function place(el) {
  const text = el.__hintText;
  if (!text) { hide(el); return; }
  const tip = ensureTipEl();
  tip.textContent = text;
  tip.className = 'sys-tip pos-top';
  tip.style.display = 'block';
  tip.style.visibility = 'hidden';
  // Measure from the origin. .sys-tip is position:fixed with max-width, so its used
  // width is capped by (viewport − left); measuring while last hover's `left` is still
  // applied made a long hint wrap into a tall narrow column and land ~100px too high.
  tip.style.left = '0px';
  tip.style.top = '0px';

  const r = el.getBoundingClientRect();
  const gap = 10;
  const tw = tip.offsetWidth;
  const th = tip.offsetHeight;
  // clientWidth, not innerWidth — the latter includes a classic vertical scrollbar,
  // which would let a right-clamped tip slide under it
  const vw = document.documentElement.clientWidth;

  let pos = 'pos-top';
  let top = r.top - th - gap;
  if (top < 8) {
    pos = 'pos-bottom';
    top = r.bottom + gap;
  }
  const targetCx = r.left + r.width / 2;
  let left = targetCx - tw / 2;
  left = Math.max(8, Math.min(left, vw - tw - 8));

  tip.className = 'sys-tip ' + pos;
  tip.style.left = left + 'px';
  tip.style.top = top + 'px';
  // Keep the arrow on the target even when the box was clamped at a screen edge,
  // instead of leaving it stranded at the box's centre pointing at nothing.
  tip.style.setProperty('--arrow-x', Math.max(12, Math.min(targetCx - left, tw - 12)) + 'px');
  tip.style.visibility = 'visible';
  ownerEl = el;
}

function hide(el) {
  if (!ownerEl) return;
  if (el && ownerEl !== el) return;
  ownerEl = null;
  if (tipEl) tipEl.style.display = 'none';
}

function bindGlobalDismiss() {
  if (scrollBound) return;
  scrollBound = true;
  // capture:true catches scroll inside any nested scrollable container (grids, modals),
  // not just window-level scroll — same reasoning as App.vue's .side-nav @scroll hideNavTip.
  window.addEventListener('scroll', () => hide(), true);
  window.addEventListener('resize', () => hide());
  // a drag moves the element out from under a stationary cursor without ever firing
  // mouseleave (ScheduleAdmin's class blocks), which would strand the tip mid-screen
  window.addEventListener('pointerdown', () => hide(), true);
}

export default {
  mounted(el, binding) {
    el.__hintText = binding.value;
    el.__hintShow = () => place(el);
    el.__hintHide = () => hide(el);
    el.addEventListener('mouseenter', el.__hintShow);
    el.addEventListener('mouseleave', el.__hintHide);
    el.addEventListener('focus', el.__hintShow);
    el.addEventListener('blur', el.__hintHide);
    el.addEventListener('click', el.__hintHide);
    syncA11y(el, binding.value);
    bindGlobalDismiss();
  },
  updated(el, binding) {
    if (el.__hintText === binding.value) return;
    el.__hintText = binding.value;
    syncA11y(el, binding.value);
    // re-render while the tip is up (row re-keyed, value flipped): re-run the whole
    // placement so the text and the geometry stay in step instead of going stale
    if (ownerEl === el) place(el);
  },
  unmounted(el) {
    hide(el);
    el.removeEventListener('mouseenter', el.__hintShow);
    el.removeEventListener('mouseleave', el.__hintHide);
    el.removeEventListener('focus', el.__hintShow);
    el.removeEventListener('blur', el.__hintHide);
    el.removeEventListener('click', el.__hintHide);
  },
};
